import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";

// Cart state with localStorage persistence. Mirrors the order line-item shape
// from the data model: lines[{ productId, qty, price }].

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "fp_cart_v1";

function reducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const { plant, qty } = action;
      const existing = state.find((l) => l.id === plant.id);
      if (existing) {
        return state.map((l) =>
          l.id === plant.id
            ? { ...l, qty: Math.min(l.qty + qty, plant.stock) }
            : l
        );
      }
      return [
        ...state,
        {
          id: plant.id,
          slug: plant.slug,
          name: plant.name,
          price: plant.price,
          image: plant.images[0],
          stock: plant.stock,
          qty: Math.min(qty, plant.stock),
        },
      ];
    }
    case "SET_QTY":
      return state
        .map((l) =>
          l.id === action.id
            ? { ...l, qty: Math.max(0, Math.min(action.qty, l.stock)) }
            : l
        )
        .filter((l) => l.qty > 0);
    case "REMOVE":
      return state.filter((l) => l.id !== action.id);
    case "CLEAR":
      return [];
    default:
      return state;
  }
}

function init() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [lines, dispatch] = useReducer(reducer, [], init);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* storage may be unavailable */
    }
  }, [lines]);

  const value = useMemo(() => {
    const count = lines.reduce((n, l) => n + l.qty, 0);
    const subtotal = lines.reduce((n, l) => n + l.qty * l.price, 0);
    return {
      lines,
      count,
      subtotal,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      addItem: (plant, qty = 1) => {
        dispatch({ type: "ADD", plant, qty });
        setIsOpen(true);
      },
      setQty: (id, qty) => dispatch({ type: "SET_QTY", id, qty }),
      removeItem: (id) => dispatch({ type: "REMOVE", id }),
      clear: () => dispatch({ type: "CLEAR" }),
    };
  }, [lines, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
